import type { PortfolioProblemStatementEvidence, PortfolioProblemStatementRow } from "@/services/api/case-checking";
import { compareProblemStatementIds } from "@/components/case-checking/problemStatementRuleDisplay";

export type StatementSortMode = "catalog" | "fail_rate" | "issue_rate";

export function psCatalogOrder(a: PortfolioProblemStatementRow, b: PortfolioProblemStatementRow): number {
  return compareProblemStatementIds(a.problem_statement_id, b.problem_statement_id);
}

export function sortProblemStatementRows(
  rows: PortfolioProblemStatementRow[],
  mode: StatementSortMode,
): PortfolioProblemStatementRow[] {
  const sorted = [...rows];
  if (mode === "catalog") {
    sorted.sort(psCatalogOrder);
    return sorted;
  }
  if (mode === "fail_rate") {
    sorted.sort((a, b) => b.fail_rate_pct - a.fail_rate_pct || psCatalogOrder(a, b));
    return sorted;
  }
  sorted.sort((a, b) => b.issue_rate_pct - a.issue_rate_pct || psCatalogOrder(a, b));
  return sorted;
}

export function concentrationLabel(failCount: number, applicableCount: number): string | null {
  if (applicableCount <= 0 || failCount <= 0) return null;
  if (failCount >= applicableCount) return "All cases";
  if (failCount >= Math.max(2, Math.ceil(applicableCount * 0.5))) return "Most cases";
  return null;
}

export function formatRatePct(value: number): string {
  return Number.isInteger(value) ? `${value}%` : `${value.toFixed(1)}%`;
}

export function affectedClientsForRow(row: PortfolioProblemStatementRow, colById: Map<string, string>): string[] {
  return row.cells
    .filter((c) => c.rating === "fail" || c.rating === "unclear")
    .map((c) => colById.get(c.case_id) ?? c.case_id);
}

export function evidenceInsightFromNarratives(
  evidence: PortfolioProblemStatementEvidence[],
): string | null {
  if (evidence.length === 0) return null;
  const themes = evidence.map((e) => e.text.split(/[.!?]/)[0]?.trim()).filter(Boolean);
  if (themes.length === 1) return themes[0];
  const unique = [...new Set(themes)];
  if (unique.length === 1) return unique[0];
  return `Recurring themes: ${unique.slice(0, 2).join("; ")}.`;
}

export function fallbackStatementInsight(row: PortfolioProblemStatementRow): string | null {
  const fromEvidence = evidenceInsightFromNarratives(row.evidence ?? []);
  if (fromEvidence) return fromEvidence;
  if (row.applicable_count <= 0 || row.fail_count + row.unclear_count <= 0) return null;
  return `${formatRatePct(row.issue_rate_pct)} of applicable cases flagged (${row.fail_count} No, ${row.unclear_count} Unclear).`;
}
